import {
  BranchFormSubmissionResponse,
  BranchTokenValidationApiResponse,
  BranchTokenValidationResponse,
  CombinedBranchFormData,
} from '@/types/branch.types';
import { apiService } from './api';
import { transformBranchFormDataToApi } from '@/lib/branchFormTransformers';

/**
 * Branch Form Service
 *
 * Service for handling branch form operations including token validation
 * and form submission for the public branches form
 */
export class BranchFormService {
  /**
   * Validates a token for branch form access
   * Public endpoint - no authentication required
   *
   * Uses the Branch-specific validation endpoint: GET /api/v1/branches/validate-token/{token}
   */
  async validateToken(token: string): Promise<BranchTokenValidationResponse> {
    try {
      const response = await apiService.get<BranchTokenValidationApiResponse>(
        `/branches/validate-token/${token}`
      );

      // The API returns { success: true, message: "Token is valid", result: { valid: true } }
      if (response.data?.success && response.data?.result?.valid) {
        return {
          valid: true,
          message: response.data.message || 'Token is valid',
          expires_at: response.data.result.expires_at || undefined,
        };
      }

      throw new Error(response.data?.message || 'Token validation failed');
    } catch (error: unknown) {
      // Handle API errors (401 for invalid tokens, 500 for server errors)
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            status?: number;
            data?: { message?: string };
          };
        };

        if (apiError.response?.status === 401) {
          throw new Error(
            apiError.response.data?.message || 'Token is invalid or expired'
          );
        }

        // Re-throw other API errors with message
        throw new Error(
          apiError.response?.data?.message || 'Token validation failed'
        );
      }

      // Handle network or other errors
      throw new Error('Network error during token validation');
    }
  }

  /**
   * Submits branch form data
   * Public endpoint - uses token for validation
   *
   * Implementation using POST /api/v1/branches/submit-form
   *
   * This method automatically transforms the client-side data structure
   * to match the API schema expectations (snake_case, flattened opening hours)
   */
  async submitBranchForm(
    token: string,
    data: CombinedBranchFormData
  ): Promise<BranchFormSubmissionResponse> {
    try {
      // Transform client-side data to API format
      const apiPayload = transformBranchFormDataToApi(
        token,
        data.branches || [],
        data.branchContacts || []
      );

      const response = await apiService.post(
        '/branches/submit-form',
        apiPayload
      );
      return response.data as BranchFormSubmissionResponse;
    } catch (error: unknown) {
      // Handle API errors
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            data?: {
              message?: string;
              errors?: Record<string, string[]>;
            };
            status?: number;
          };
        };

        // Handle validation errors (422)
        if (
          apiError.response?.status === 422 &&
          apiError.response?.data?.errors
        ) {
          const validationError = new Error('Validation failed');
          (
            validationError as unknown as { errors: Record<string, string[]> }
          ).errors = apiError.response.data.errors;
          throw validationError;
        }

        // Handle invalid token (401)
        if (apiError.response?.status === 401) {
          throw new Error(
            apiError.response?.data?.message || 'Invalid or expired token'
          );
        }

        throw new Error(
          apiError.response?.data?.message || 'Form submission failed'
        );
      }
      throw new Error('Network error during form submission');
    }
  }
}

// Singleton instance
export const branchFormService = new BranchFormService();
